import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, ClipboardList, Download, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PrBadge } from '@/components/pr/PrBadge';
import { PrFgFilter } from '@/components/pr/PrFgFilter';
import { PrItemsRow } from '@/components/pr/PrItemsRow';
import { PrOverdueBanner } from '@/components/pr/PrOverdueBanner';
import { exportPendingPrCsv } from '@/components/pr/prCsv';
import { fmtQty, prApi, prErr, unitNote } from '@/services/prApi';

const TABS = [['OPEN,PARTLY_ORDERED', 'To order'], ['overdue', 'Overdue'], ['DRAFT', 'Drafts'], ['FULLY_ORDERED', 'Fully ordered'], ['CLOSED,CANCELLED', 'Closed / cancelled'], ['', 'All']];
const HEAD = ['', 'Requisition', 'Section', 'Required by', 'Items', 'Required', 'On POs', 'Still to order', 'Status'];
const query = (tab, q, fg) => ({ ...(tab === 'overdue' ? { overdue: true } : { status: tab || undefined }), q: q.trim() || undefined, fg_name: fg || undefined });

const ExportBtn = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const n = await exportPendingPrCsv();
      if (n) toast.success(`Exported ${n} pending line${n === 1 ? '' : 's'}`); else toast.info('No pending requisition lines — the CSV only has headers');
    } catch (e) { toast.error(prErr(e, 'Could not export')); } finally { setBusy(false); }
  };
  return <Button variant="outline" onClick={run} disabled={busy} data-testid="pr-export-pending-csv">
    {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}Pending CSV</Button>;
};

const FgLine = ({ p }) => (p.fg_line ? (
  <div className="text-[10px] font-medium text-amber-700" data-testid={`pr-row-fg-line-${p.pr_id}`}>this FG: {fmtQty(p.fg_line.required_qty)} {p.fg_line.unit} · {fmtQty(p.fg_line.balance_qty)} to order</div>) : null);

const PrRow = ({ p, onOpen, open, onToggle }) => (
  <TableRow className="cursor-pointer hover:bg-indigo-50/50" onClick={onOpen} data-testid={`pr-row-${p.pr_id}`}>
    <TableCell className="w-8 px-2"><button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} className="rounded p-1 text-gray-500 hover:bg-indigo-100 hover:text-indigo-700"
      aria-label="Show FG lines" data-testid={`pr-row-expand-${p.pr_id}`}>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button></TableCell>
    <TableCell><div className="font-mono text-sm font-medium">{p.pr_number}</div><div className="text-[11px] text-gray-500">{p.pr_date} · {p.created_by_name}</div></TableCell>
    <TableCell className="text-sm">{p.section_name}</TableCell>
    <TableCell className="text-sm">{p.required_by_date || '—'}
      {p.days_late > 0 && <div className="text-[11px] font-semibold text-rose-600" data-testid={`pr-row-late-${p.pr_id}`}>{p.days_late}d late</div>}</TableCell>
    <TableCell className="text-right tabular-nums">{p.item_count}</TableCell>
    <TableCell className="text-right tabular-nums">{fmtQty(p.total_required_qty)}{unitNote(p.units) && <div className="text-[10px] text-gray-400">{unitNote(p.units)}</div>}<FgLine p={p} /></TableCell>
    <TableCell className="text-right tabular-nums text-indigo-700">{fmtQty(p.total_ordered_qty)}</TableCell>
    <TableCell className={`text-right tabular-nums ${p.total_balance_qty > 0 ? 'font-semibold text-amber-700' : ''}`} data-testid={`pr-row-balance-${p.pr_id}`}>{fmtQty(p.total_balance_qty)}</TableCell>
    <TableCell><PrBadge status={p.status} testId={`pr-row-status-${p.pr_id}`} /></TableCell>
  </TableRow>
);

export default function PRList() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'overdue' ? 'overdue' : TABS[0][0]);
  const [q, setQ] = useState('');
  const [fg, setFg] = useState('');
  const [rows, setRows] = useState(null);
  const [openIds, setOpenIds] = useState({});
  useEffect(() => {
    setRows(null);
    const t = setTimeout(() => prApi.list(query(tab, q, fg)).then(setRows)
      .catch((e) => toast.error(prErr(e, 'Could not load requisitions'))), 250);
    return () => clearTimeout(t);
  }, [tab, q, fg]);
  const toggle = (id) => setOpenIds((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pr-list-page">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b bg-white px-4 pb-3 pt-12 sm:pt-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="pr-list-back"><ArrowLeft className="h-4 w-4" /></Button>
        <ClipboardList className="hidden h-5 w-5 text-indigo-600 sm:block" />
        <div className="flex-1"><h1 className="text-lg font-bold text-gray-900">Purchase Requisitions</h1>
          <p className="text-xs text-gray-500">Requirements posted without a supplier — every Purchase Order is raised against them</p></div>
        <ExportBtn />
        <Button onClick={() => navigate('/pr/create')} data-testid="pr-new-btn"><Plus className="mr-1 h-4 w-4" />New requisition</Button>
      </div>
      <div className="mx-auto max-w-7xl space-y-4 p-4">
        {tab !== 'overdue' && <PrOverdueBanner onShowAll={() => setTab('overdue')} />}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap rounded-md border bg-white p-0.5" data-testid="pr-tabs">
            {TABS.map(([v, l]) => <button key={l} type="button" onClick={() => setTab(v)} data-testid={`pr-tab-${l.split(' ')[0].toLowerCase()}`}
              className={`rounded px-3 py-1.5 text-sm transition-colors ${tab === v ? (v === 'overdue' ? 'bg-rose-600 text-white' : 'bg-indigo-600 text-white') : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>)}
          </div>
          <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requisition no, section, design name, FG" className="bg-white pl-9" data-testid="pr-search" /></div>
          <PrFgFilter value={fg} onChange={setFg} />
        </div>
        <Card><CardContent className="overflow-x-auto p-0">
          <Table data-testid="pr-table">
            <TableHeader><TableRow>{HEAD.map((h, i) => <TableHead key={h || 'x'} className={i >= 4 && i <= 7 ? 'text-right' : ''}>{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {!rows && <TableRow><TableCell colSpan={HEAD.length} className="py-10 text-center text-gray-400">Loading…</TableCell></TableRow>}
              {rows && !rows.length && <TableRow><TableCell colSpan={HEAD.length} className="py-10 text-center text-gray-500" data-testid="pr-empty">
                {tab === 'overdue' ? 'Nothing overdue — every open requisition is within its required-by date' : 'No requisitions here'}</TableCell></TableRow>}
              {(rows || []).map((p) => (
                <Fragment key={p.pr_id}>
                  <PrRow p={p} open={!!openIds[p.pr_id]} onToggle={() => toggle(p.pr_id)} onOpen={() => navigate(`/pr/${p.pr_id}`)} />
                  {openIds[p.pr_id] && <PrItemsRow prId={p.pr_id} colSpan={HEAD.length} fgName={fg} />}
                </Fragment>))}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>
    </div>
  );
}
